import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2EmptyState,
  V2FormActions,
  V2FormGrid,
  V2FormField,
  V2DataGrid,
  Icon,
  LoadingState,
  StatePanel,
  Combobox,
  type ComboboxOption,
  type V2BadgeTone,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost, ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { PartyLink } from './PartyLink.js';
import { amountColumnHeader, formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay, useTenantSettings } from '../../lib/tenantSettings.js';
import { vatEnabled } from '../../lib/types/tenantSettings.js';
import type {
  FulfillmentStatus,
  QuoteListResponse,
  SalesOrder,
  SalesOrderListResponse,
} from '../../lib/types/crm.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';

const FULFILLMENT_LABEL: Record<FulfillmentStatus, string> = {
  unreserved: 'رزرو نشده',
  partially_reserved: 'رزرو جزئی',
  ready_to_deliver: 'آماده تحویل',
  partially_delivered: 'تحویل جزئی',
  fully_delivered: 'تحویل کامل',
};

const FULFILLMENT_TONE: Record<FulfillmentStatus, V2BadgeTone> = {
  unreserved: 'neutral',
  partially_reserved: 'warn',
  ready_to_deliver: 'brand',
  partially_delivered: 'warn',
  fully_delivered: 'success',
};

interface Row extends SalesOrder {
  readonly id: string;
}

function deliverySummary(row: SalesOrder): string {
  const fulfillment = row.fulfillment;
  if (!fulfillment || fulfillment.lineCount === 0) return 'بدون ردیف کالا';
  if (fulfillment.lines.length === 1) {
    const line = fulfillment.lines[0]!;
    return `${line.deliveredQuantity} از ${line.orderedQuantity} ${line.uomRef}`;
  }
  return `${fulfillment.fullyDeliveredLineCount.toLocaleString('fa-IR')} از ${fulfillment.lineCount.toLocaleString('fa-IR')} ردیف کامل`;
}

interface LineDraft {
  productCode: string;
  description: string;
  quantity: string;
  uomCode: string;
  unitPriceRial: string;
  discountRial: string;
  taxCode: string;
}

const EMPTY_LINE: LineDraft = {
  productCode: '',
  description: '',
  quantity: '',
  uomCode: '',
  unitPriceRial: '',
  discountRial: '',
  taxCode: '',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Sales CRM.dc.html §06 SALES ORDERS
 * archetype: ENTITY_LIST + create form
 * status: KIT_CONFORMANT. The fulfillment/delivery columns are real and
 *   inventory-derived (ERP-157, `GET .../sales/orders` embeds each row's
 *   `fulfillment`).
 *
 * BUG-08 reversal: ERP-157 replaced the create form here with a pure
 * read-only list ("Read-only sales-order fulfillment surface") while it
 * built the real fulfillment reporting. That left no way to create a
 * sales order from the UI at all, which this QA pass names a P0/P1
 * contract violation for a chain the demo needs to actually walk through.
 * This restores order creation alongside ERP-157's reporting columns: a
 * quote conversion (picking one copies its lines server-side, per
 * createSalesOrder's own copy-from-quote path — no lines need entering
 * here) or real manual lines for a standalone order
 * (packages/modules/crm-sales/src/sales-lines.ts, same as quotes).
 * state reference: ready · empty · permission_denied · network_error
 */
export function OrdersPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const showVat = vatEnabled(useTenantSettings());
  const { unit } = useAmountDisplay();
  const [query, setQuery] = useState('');
  const [searchParams] = useSearchParams();
  // Arriving from a customer's file («سفارش جدید»): the customer is already chosen.
  const [partyRef, setPartyRef] = useState(searchParams.get('customer') ?? '');
  const [quoteRef, setQuoteRef] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [refreshKey, setRefreshKey] = useState(0);

  const orders = useApiGet<SalesOrderListResponse>(
    slug ? `${base}/api/v1/sales/orders?limit=200` : null,
    [slug, refreshKey],
  );
  // BUG-10: only customers belong in a sales order's party picker.
  const parties = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/parties?role=customer&limit=200` : null,
    [slug],
  );
  const quotes = useApiGet<QuoteListResponse>(
    slug ? `${base}/api/v1/sales/quotes?limit=200` : null,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?status=active&limit=200` : null,
    [slug],
  );
  const uoms = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/uoms?status=active&limit=200` : null,
    [slug],
  );
  const taxes = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/taxes?status=active&limit=200` : null,
    [slug],
  );

  const partyOptions = useMemo<ComboboxOption[]>(() => {
    if (parties.status !== 'ready') return [];
    return parties.data.items.map((p) => ({
      value: String(p.code),
      label: `${p.code} — ${p.name ?? p.code}`,
    }));
  }, [parties]);

  const filtered = useMemo(() => {
    if (orders.status !== 'ready') return [];
    const value = query.trim().toLowerCase();
    if (!value) return orders.data.orders;
    return orders.data.orders.filter(
      (row) =>
        row.ref.toLowerCase().includes(value) || (row.partyRef ?? '').toLowerCase().includes(value),
    );
  }, [orders, query]);

  if (!slug) return null;

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, { ...EMPTY_LINE }]);
  }
  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }
  function resetForm() {
    setPartyRef('');
    setQuoteRef('');
    setLines([{ ...EMPTY_LINE }]);
    setFieldErrors({});
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    // Converting a quote copies its lines server-side — manual lines are
    // only required for a standalone order (no quoteRef).
    const validLines = lines.filter((l) => l.productCode && l.quantity && l.uomCode);
    if (!quoteRef && validLines.length === 0) {
      setError('برای سفارش مستقل، حداقل یک قلم با کالا، مقدار و واحد لازم است.');
      setSubmitting(false);
      return;
    }
    try {
      await apiPost(`${base}/api/v1/sales/orders`, {
        partyRef: partyRef || undefined,
        quoteRef: quoteRef || undefined,
        lines: quoteRef
          ? undefined
          : validLines.map((l) => ({
              productRef: l.productCode,
              description: l.description.trim() || undefined,
              quantity: l.quantity,
              uomRef: l.uomCode,
              unitPriceRial: l.unitPriceRial.trim() || undefined,
              discountRial: l.discountRial.trim() || undefined,
              taxRef: l.taxCode || undefined,
            })),
      });
      resetForm();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const details = err.envelope.error.details;
        const newFieldErrors: Record<string, string> = {};
        if (details && details.length > 0) {
          for (const item of details) {
            newFieldErrors[item.path] = item.message;
          }
        }
        if (err.envelope.error.code === 'PARTY_ROLE_INVALID' && !newFieldErrors['partyRef']) {
          newFieldErrors['partyRef'] = err.envelope.error.message;
        }
        setFieldErrors(newFieldErrors);
        setError(err.envelope.error.message);
      } else {
        setError('ثبت سفارش ناموفق بود.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const productOptions = products.status === 'ready' ? products.data.items : [];
  const uomOptions = uoms.status === 'ready' ? uoms.data.items : [];
  const taxOptions = taxes.status === 'ready' ? taxes.data.items : [];

  const columns: readonly V2TableColumn<Row>[] = [
    {
      key: 'ref',
      header: 'شماره سفارش',
      render: (row) => (
        <Link to={`${base}/crm/orders/${row.ref}`} className="ltr">
          {row.ref}
        </Link>
      ),
    },
    {
      key: 'partyRef',
      header: 'طرف‌حساب',
      render: (row) =>
        row.partyRef ? (
          <PartyLink base={base} partyRef={row.partyRef} inRow>
            {row.partyRef}
          </PartyLink>
        ) : (
          '—'
        ),
    },
    {
      key: 'fulfillment',
      header: 'وضعیت تأمین و تحویل',
      render: (row) =>
        row.fulfillment ? (
          <V2Badge tone={FULFILLMENT_TONE[row.fulfillment.fulfillmentStatus]}>
            {FULFILLMENT_LABEL[row.fulfillment.fulfillmentStatus]}
          </V2Badge>
        ) : (
          '—'
        ),
    },
    { key: 'delivery', header: 'تحویل‌شده', render: deliverySummary },
    {
      key: 'totalRial',
      header: amountColumnHeader('مبلغ سفارش', unit),
      align: 'end',
      render: (row) => <span className="sk-num">{formatRialFa(row.totalRial, unit)}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {orders.status === 'loading' ? <LoadingState /> : null}
      {orders.status === 'error' ? (
        <StatePanel
          variant={orders.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={
            orders.error.status === 403
              ? 'دسترسی مشاهده سفارش‌های فروش را ندارید'
              : 'فهرست سفارش‌ها قابل نمایش نیست'
          }
        />
      ) : null}

      {orders.status === 'ready' ? (
        <V2Card>
          <form
            onSubmit={onCreate}
            style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <V2FormGrid>
              <V2FormField label="طرف‌حساب" width="wide" error={fieldErrors['partyRef']}>
                <Combobox
                  options={partyOptions}
                  value={partyRef}
                  onValueChange={(val) => {
                    setPartyRef(val);
                    setFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next['partyRef'];
                      return next;
                    });
                  }}
                  placeholder="جست‌وجوی طرف‌حساب (کد یا نام)..."
                  disabled={parties.status === 'loading'}
                />
              </V2FormField>
              <V2FormField label="پیش‌فاکتور" width="medium">
                {quotes.status === 'ready' ? (
                  <select
                    className="sk-select"
                    value={quoteRef}
                    onChange={(e) => setQuoteRef(e.target.value)}
                  >
                    <option value="">— بدون پیش‌فاکتور —</option>
                    {quotes.data.quotes.map((q) => (
                      <option key={q.ref} value={q.ref}>
                        {q.ref}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="sk-input ltr"
                    value={quoteRef}
                    onChange={(e) => setQuoteRef(e.target.value)}
                  />
                )}
              </V2FormField>
            </V2FormGrid>

            {quoteRef ? (
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 'var(--sk-r-md)',
                  background: 'var(--sk-bg-canvas)',
                  border: '1px solid var(--sk-border)',
                  fontSize: 12,
                  color: 'var(--sk-fg-muted)',
                }}
              >
                اقلام این سفارش از پیش‌فاکتور {quoteRef} کپی می‌شود.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>اقلام سفارش</span>
                    <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                      کالا، مقدار و واحد برای هر قلم الزامی است؛ جمع‌ها سمت سرور محاسبه می‌شود.
                    </span>
                  </div>
                  <V2Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon="plus"
                    onClick={addLine}
                  >
                    افزودن قلم
                  </V2Button>
                </div>

                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 160 }}>کالا *</th>
                        <th style={{ minWidth: 120 }}>شرح</th>
                        <th style={{ width: 90 }}>مقدار *</th>
                        <th style={{ width: 110 }}>واحد *</th>
                        <th style={{ width: 120 }}>فی (ریال)</th>
                        <th style={{ width: 110 }}>تخفیف (ریال)</th>
                        {showVat ? <th style={{ width: 130 }}>مالیات</th> : null}
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, i) => (
                        <tr key={i}>
                          <td>
                            <select
                              className="sk-select"
                              style={{ width: '100%' }}
                              value={line.productCode}
                              onChange={(e) => updateLine(i, { productCode: e.target.value })}
                            >
                              <option value="">— انتخاب کالا —</option>
                              {productOptions.map((p) => (
                                <option key={String(p['code'])} value={String(p['code'])}>
                                  {String(p['name'] ?? p['code'])}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="sk-input"
                              style={{ width: '100%' }}
                              value={line.description}
                              onChange={(e) => updateLine(i, { description: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="sk-input ltr sk-num"
                              style={{ width: '100%' }}
                              value={line.quantity}
                              onChange={(e) => updateLine(i, { quantity: e.target.value })}
                            />
                          </td>
                          <td>
                            <select
                              className="sk-select"
                              style={{ width: '100%' }}
                              value={line.uomCode}
                              onChange={(e) => updateLine(i, { uomCode: e.target.value })}
                            >
                              <option value="">— واحد —</option>
                              {uomOptions.map((u) => (
                                <option key={String(u['code'])} value={String(u['code'])}>
                                  {String(u['name'] ?? u['code'])}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="sk-input ltr sk-num"
                              style={{ width: '100%' }}
                              value={line.unitPriceRial}
                              onChange={(e) => updateLine(i, { unitPriceRial: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              className="sk-input ltr sk-num"
                              style={{ width: '100%' }}
                              value={line.discountRial}
                              onChange={(e) => updateLine(i, { discountRial: e.target.value })}
                            />
                          </td>
                          {showVat ? (
                            <td>
                              <select
                                className="sk-select"
                                style={{ width: '100%' }}
                                value={line.taxCode}
                                onChange={(e) => updateLine(i, { taxCode: e.target.value })}
                              >
                                <option value="">— بدون مالیات —</option>
                                {taxOptions.map((t) => (
                                  <option key={String(t['code'])} value={String(t['code'])}>
                                    {String(t['name'] ?? t['code'])}
                                  </option>
                                ))}
                              </select>
                            </td>
                          ) : null}
                          <td>
                            {lines.length > 1 ? (
                              <button
                                type="button"
                                className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                                style={{ color: 'var(--sk-danger)' }}
                                onClick={() => removeLine(i)}
                              >
                                <Icon name="x" size="sm" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <V2FormActions>
              {error ? (
                <span style={{ fontSize: 13, color: 'var(--sk-danger)', marginInlineEnd: 'auto' }}>
                  {error}
                </span>
              ) : null}
              <V2Button type="submit" variant="primary" icon="plus" disabled={submitting}>
                {submitting ? 'در حال ثبت…' : 'سفارش جدید'}
              </V2Button>
            </V2FormActions>
          </form>
        </V2Card>
      ) : null}

      {orders.status === 'ready' ? (
        <V2DataGrid
          icon="cart"
          title="سفارش‌های فروش"
          subtitle={`${(!query.trim() && (orders.data as any)?.total !== undefined
            ? (orders.data as any).total
            : filtered.length
          ).toLocaleString('fa-IR')} سفارش`}
          search={{ value: query, onChange: setQuery, placeholder: 'شماره سفارش یا طرف‌حساب' }}
          columns={columns}
          rows={filtered.map((order) => ({ ...order, id: order.ref }))}
          rowKey={(order) => order.id}
          zebra
          emptyState={
            <V2EmptyState
              icon="cart"
              title={query ? 'سفارشی با این جست‌وجو پیدا نشد' : 'سفارش فروشی ثبت نشده است'}
              description={
                query
                  ? undefined
                  : 'سفارش فروش جدید را از فرم بالا یا با تبدیل یک پیش‌فاکتور ثبت کنید.'
              }
              compact
            />
          }
        />
      ) : null}
    </div>
  );
}
